import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  HStack,
  AlertIcon,
  Alert,
  Button,
  Spacer,
  Wrap,
  WrapItem,
  Icon,
  Heading,
} from '@chakra-ui/react';
import DayPicker, { DateUtils, Modifiers } from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import { DayPickerContainer } from '../../common/DayPicker/DayPickerStyled';
import { DayPickerNavBar } from '../../common/DayPicker/DayPickerNavBar';
import { DayPickerDay } from '../../common/DayPicker/DayPickerDay';
import {
  getDisabledDays,
  getDisabledDaysBeforeToday,
  getInitialDateRangeHoverState,
  getModifiers,
  canBeSelected,
  hasSelectedOverBookedUp,
  isSelectingFrom,
  isSelectingToAgain,
  isSelectingTo,
  getBookedUpDateFromToday,
} from '../../../services/Date/DateRangeService';
import { DayModifiers, RangeModifier } from 'react-day-picker/types/Modifiers';
import 'react-day-picker/lib/style.css';
import { ModalView } from './ModalView';
import moment from 'moment';
import { ObjectResponse, SingleObject } from '../../../types/ObjectTypes';
import { ObjectBase } from '../../../types/ObjectTypes';
import { Number } from '../../common/Format/Number';
import { IoCalendarOutline } from 'react-icons/io5';
import { Flex } from '@chakra-ui/layout';
import { ExtendedDetailsRoute } from '../../../types/RoutesTypes';
import { isQualifiedToFetchWithExtendedParams } from '../../../services/Object/ObjectService';
import { DayPickerRootContainer } from '../../../styles/DayPickerStyled';
import { BookingCustomer } from '../../../types/BookingTypes';
import { AxiosResponse } from 'axios';
import { getAxiosWithConfig } from '../../../services/Http/HttpService';
import { getInitialCustomerParams } from '../../../services/Booking/BookingService';

export interface BookingCalendarProps {
  error?: string;
  object: SingleObject;
  baseData: ObjectBase;
  isTabletOrMobile: boolean;
  handleDateRangeChange: (fromDate: string | undefined, toDate: string | undefined) => void;
  handleRedirectToBooking: () => void;
  routeParams: ExtendedDetailsRoute;
  switchBookingCalendarModal: boolean;
  handleBookingCalendarModal: () => void;
}

export const BookingCalendar = ({
  isTabletOrMobile,
  baseData,
  handleDateRangeChange,
  handleRedirectToBooking,
  error,
  object,
  routeParams,
  switchBookingCalendarModal,
  handleBookingCalendarModal,
}: BookingCalendarProps): JSX.Element => {
  const { vacationsData, priceData } = object;
  const [duration, setDuration] = useState<string>('');
  const [initialMonth, setInitialMonth] = useState<Date | undefined>();
  const [calendarPreviewRange, setCalendarPreviewRange] = useState<number>(3);
  const [selectedDateRange, setSelectedDateRange] = useState<RangeModifier>(
    getInitialDateRangeHoverState(),
  );
  const [disabledDays, setDisabledDays] = useState<Partial<Modifiers>>();
  const [modifiers, setModifiers] = useState<Partial<Modifiers>>();
  const [bookedUpDates, setBookedUpDates] = useState<any>(
    vacationsData
      ? vacationsData?.map(vacation => {
          return { from: new Date(vacation.fromDate), to: new Date(vacation.toDate) };
        })
      : [],
  );
  const [bookingContinueButtonText, setBookingContinueButtonText] = useState('');
  const [isPhoneButtonEnabled, setPhoneButtonEnabled] = useState(false);
  const [customerPhone, setCustomerPhone] = useState('');

  const minDays = object.priceData ? object.priceData.RENTAL.numberOfDays : 0;

  const selectedDays = useMemo(() => {
    return [selectedDateRange.from, { from: selectedDateRange.from, to: selectedDateRange.to }];
  }, [selectedDateRange]);

  const handleCalendarPreviewRange = useCallback((value: number) => {
    setCalendarPreviewRange(value);
  }, []);

  const handleDayClick = useCallback(
    (day: Date, modifiers: DayModifiers) => {
      const range = DateUtils.addDayToRange(day, selectedDateRange);

      if (!canBeSelected(modifiers)) {
        return;
      }

      if (hasSelectedOverBookedUp(range, modifiers as any, disabledDays)) {
        setSelectedDateRange({ from: day, to: day });
        setDisabledDays(getDisabledDaysBeforeToday(disabledDays));
        setDuration('Reisedauer: ' + 0 + ' Tage');
      } else {
        if (isSelectingFrom(selectedDateRange)) {
          setDisabledDays(getDisabledDaysBeforeToday(disabledDays));
          setSelectedDateRange(range);
        } else if (isSelectingTo(selectedDateRange)) {
          setDisabledDays(getDisabledDaysBeforeToday(disabledDays));
          setSelectedDateRange(range);
          handleDateRangeChange(
            moment(range.from).format('YYYY-MM-DD'),
            moment(range.to).format('YYYY-MM-DD'),
          );
          setDuration('Reisedauer: ' + moment(range.to).diff(range.from, 'days') + ' Tage');
        } else if (isSelectingToAgain(selectedDateRange)) {
          setSelectedDateRange({ from: undefined, to: undefined });
          setDuration('Reisedauer: ' + 0 + ' Tage');
          handleDateRangeChange(undefined, undefined);
        }
      }
    },
    [selectedDateRange, disabledDays],
  );

  const fetchObject = async (
    objectParams: BookingCustomer,
  ): Promise<AxiosResponse<ObjectResponse>> => {
    try {
      const instance = getAxiosWithConfig();
  
      return instance.post('plugin', {
        plugin: 'basic',
        data: {
          module: 'agency',
          params: {
            "crud": "fetchAll"               
        },
        },
      });
    } catch (e) {
      throw new Error(e);
    }
  };

  useEffect(() => {
    (async function() {
      const objectParams = getInitialCustomerParams();
      const objectDetailData = await fetchObject(objectParams);
     
      setCustomerPhone(objectDetailData.data.data.result.response[0].Phone1);
    })()
  }, [customerPhone]);
  
  useEffect(() => {
    setPhoneButtonEnabled(false);

    if (baseData.OnlineBooking && baseData.OnlineOffer && !baseData.OnlineRequest) {
      setBookingContinueButtonText('Direkt buchen / Angebot erstellen');
    }
    if (baseData.OnlineBooking && baseData.OnlineOffer && baseData.OnlineRequest) {
      setBookingContinueButtonText('Direkt buchen / Anfrage erstellen');
    }
    if (baseData.OnlineBooking && !baseData.OnlineOffer) {
      setBookingContinueButtonText('Direkt buchen');
    }
    if (!baseData.OnlineBooking && baseData.OnlineOffer && !baseData.OnlineRequest) {
      setBookingContinueButtonText('Angebot erstellen');
    }
    if (!baseData.OnlineBooking && baseData.OnlineOffer && baseData.OnlineRequest) {
      setBookingContinueButtonText('Anfrage erstellen');
    }
    if(!baseData.OnlineBooking && !baseData.OnlineOffer) {
      setPhoneButtonEnabled(true);
      setBookingContinueButtonText('Jetzt anrufen und buchen - Tel: ' + customerPhone);
    }
    
  },[setBookingContinueButtonText, setPhoneButtonEnabled, customerPhone]);

  useEffect(() => {
    if (vacationsData) {
      const bookedUpDates = vacationsData.map(vacation => {
        return { from: new Date(vacation.fromDate), to: new Date(vacation.toDate) };
      });

      setDisabledDays(getDisabledDays(bookedUpDates, minDays, selectedDateRange));
      setBookedUpDates(bookedUpDates);
    }
  }, [vacationsData]);

  useEffect(() => {
    if (vacationsData) {
      const bookedUpDates = vacationsData
        .filter(vacation => {
          return moment(vacation.toDate).isAfter(new Date());
        })
        .map(vacation => {
          return { from: new Date(vacation.fromDate), to: new Date(vacation.toDate) };
        });

      setBookedUpDates(bookedUpDates);

      setModifiers(
        getModifiers(
          selectedDateRange,
          bookedUpDates,
          minDays,
          object.seasonsPriceData ? object.seasonsPriceData : [],
          {
            potentialArrivalDay:
              !selectedDateRange || (!!selectedDateRange && !selectedDateRange.from),
            potentialDepartureDay:
              !selectedDateRange ||
              (!!selectedDateRange && !!selectedDateRange.from && !selectedDateRange.to),
          },
        ),
      );
    }
  }, [vacationsData, selectedDateRange]);

  useEffect(() => {
    if (
      routeParams.fromDate &&
      routeParams.toDate &&
      isQualifiedToFetchWithExtendedParams(routeParams)
    ) {
      setSelectedDateRange({
        from: new Date(routeParams.fromDate),
        to: new Date(routeParams.toDate),
      });
      setInitialMonth(new Date(routeParams.fromDate));
      setDuration(
        'Reisedauer: ' + moment(routeParams.toDate).diff(routeParams.fromDate, 'days') + ' Tage',
      );
    }
  }, [routeParams]);

  const handleResetSelectedDateRange = useCallback(() => {
    setSelectedDateRange({ from: undefined, to: undefined });
    handleDateRangeChange(undefined, undefined);
  }, [setSelectedDateRange]);

  const dayPicker = useMemo(
    () => (
      <DayPickerRootContainer>
        <DayPickerContainer>
          <DayPicker
            month={initialMonth}
            className={'BookingCalendar'}
            renderDay={(date, modifiers) => (
              <DayPickerDay
                date={date}
                modifiers={modifiers}
                selectedDateRange={selectedDateRange}
                showTooltips={true}
              />
            )}
            navbarElement={props => {
              return (
                <DayPickerNavBar
                  {...props}
                  header={
                    <VStack>
                      <Box>
                        {(!selectedDateRange.from || !selectedDateRange.to) && (
                          <Alert status="warning" borderRadius={4}>
                            <AlertIcon />
                            Bitte wählen Sie einen Reisezeitraum aus.
                          </Alert>
                        )}

                        {!!selectedDateRange.from && !!selectedDateRange.to && (
                          <Wrap>
                            <Text>{duration}</Text>{' '}
                            <Button
                              size={'xs'}
                              colorScheme={'blue'}
                              variant={'outline'}
                              onClick={handleResetSelectedDateRange}
                              disabled={!selectedDateRange.from || !selectedDateRange.to}>
                              Auswahl löschen
                            </Button>
                          </Wrap>
                        )}
                      </Box>

                      <HStack width={'100%'} alignItems={'baseline'} pt={{ base: 2, md: 0 }}>
                        <Spacer flex={1} />
                        <Wrap w={'100%'} justify={'center'}>
                          <WrapItem>
                            <Button
                              colorScheme={'blue'}
                              size={'sm'}
                              variant={calendarPreviewRange === 3 ? 'outline' : 'solid'}
                              onClick={() => handleCalendarPreviewRange(3)}>
                              3 Monate
                            </Button>
                          </WrapItem>
                          <WrapItem>
                            <Button
                              colorScheme={'blue'}
                              size={'sm'}
                              variant={calendarPreviewRange === 6 ? 'outline' : 'solid'}
                              onClick={() => handleCalendarPreviewRange(6)}>
                              6 Monate
                            </Button>
                          </WrapItem>
                          <WrapItem>
                            <Button
                              colorScheme={'blue'}
                              size={'sm'}
                              variant={calendarPreviewRange === 12 ? 'outline' : 'solid'}
                              onClick={() => handleCalendarPreviewRange(12)}>
                              12 Monate
                            </Button>
                          </WrapItem>
                        </Wrap>
                      </HStack>
                    </VStack>
                  }
                />
              );
            }}
            modifiers={modifiers as any}
            selectedDays={selectedDays as any}
            localeUtils={MomentLocaleUtils}
            locale={'de'}
            onDayClick={handleDayClick}
            numberOfMonths={calendarPreviewRange}
            disabledDays={disabledDays as any}
          />
        </DayPickerContainer>
      </DayPickerRootContainer>
    ),
    [modifiers, handleDayClick, calendarPreviewRange, disabledDays, selectedDateRange],
  );

  return (
    <Box
      pl={{ sm: 0, md: 4, lg: 4, xl: 4 }}
      pr={{ sm: 0, md: 4, lg: 4, xl: 4 }}
      pt={{ sm: 2, md: 4, lg: 4, xl: 4 }}
      pb={{ sm: 2, md: 4, lg: 4, xl: 4 }}
      borderRadius={{ sm: 0, md: 4, lg: 4, xl: 4 }}
      borderWidth={1}
      borderColor={'gray.300'}
      backgroundColor={'white'}
      w={'100%'}
      id={'detailsBookingCalendar'}>
      <VStack alignItems={'flex-start'}>
        {!isTabletOrMobile && (
          <Text w={'100%'} textAlign={'left'} fontSize={'xl'} textStyle={'category.primary'}>
            Reisedaten und Buchung
          </Text>
        )}
        {isTabletOrMobile && (
          <ModalView
            selectedDateRange={selectedDateRange}
            textBtn={'Anreise wählen'}
            value={
              <Text color={'blue.600'}>
                <Icon fontSize={'xl'} as={IoCalendarOutline} />{' '}
                {moment(selectedDateRange.from).format('DD.MM.YYYY')} -{' '}
                <Icon fontSize={'xl'} as={IoCalendarOutline} />{' '}
                {moment(selectedDateRange.to).format('DD.MM.YYYY')}
              </Text>
            }
            handleBookingCalendarModal={handleBookingCalendarModal}
            switchBookingCalendarModal={switchBookingCalendarModal}>
            <>{dayPicker}</>
          </ModalView>
        )}

        {!isTabletOrMobile && dayPicker}
        {(error || priceData) && (
          <Flex width={'100%'}>
            {error && (
              <Alert status="error" borderRadius={4}>
                <AlertIcon />
                <Text>{error}</Text>
              </Alert>
            )}
            {priceData && (
              <Alert status="success" borderRadius={4} w={'100%'}>
                <Wrap>
                  <VStack width={'100%'} alignItems={'flex-start'}>
                    <Wrap>
                      <AlertIcon />
                      <Heading color={'green.800'} size={'md'} fontWeight={'semibold'}>
                        Buchung noch verfügbar!
                      </Heading>
                    </Wrap>

                    <Wrap flex={1}>
                      <Text color={'green.800'} fontWeight={'semibold'}>
                        Reisezeitraum:
                      </Text>
                      <Text color={'green.800'}>
                        <Icon fontSize={'xl'} as={IoCalendarOutline} />{' '}
                        {moment(selectedDateRange.from).format('DD.MM.YYYY')} -{' '}
                        <Icon fontSize={'xl'} as={IoCalendarOutline} />{' '}
                        {moment(selectedDateRange.to).format('DD.MM.YYYY')}
                      </Text>
                    </Wrap>
                    {!object.baseData.DisableOnlinePriceCalculation &&
                      <Wrap>
                        <Text color={'green.800'} fontWeight={'semibold'}>
                          Preis:{' '}
                        </Text>
                        <Text color={'green.800'}>
                          ab{' '}
                          <Number
                            number={priceData.COMMON.totalPrice}
                            locales={'de-De'}
                            style={'currency'}
                            currency={'EUR'}
                          />
                        </Text>
                      </Wrap>
                    }
                  </VStack>
                  <Flex alignItems={'center'}>
                    {
                      isPhoneButtonEnabled ? 
                      <a href={`tel:${customerPhone}`}>
                        <Button 
                          colorScheme={'blue'} 
                          width={'auto'}   
                          >
                          Telefonnummer: {customerPhone}
                        </Button>
                      </a>
                      :
                      <Button 
                        colorScheme={'blue'} 
                        width={'auto'}  
                        onClick={handleRedirectToBooking} 
                        >
                        {bookingContinueButtonText}
                      </Button>
                    }
                  </Flex>
                </Wrap>
              </Alert>
            )}
          </Flex>
        )}
      </VStack>
    </Box>
  );
};
